/**
 * S3-served invoice/credit-note PDF opening (AB#20717 round 2 demo-feedback fix #2).
 *
 * The backend stores a signed S3 copy of Stripe invoice PDFs and credit-note PDFs, keyed by
 * `invoice_pdf_s3_key` / `credit_note_pdf_s3_key`. Because a stored S3 object needs a freshly
 * MINTED presigned URL on every open (unlike the older `invoice_pdf_s3_url` field, which held an
 * already-signed — and eventually stale — URL directly on the row), opening one of these documents
 * is a two-step click handler:
 *
 *   1. If the row has an S3 key, hit the backend's `pdf-url` endpoint for a fresh signed URL and
 *      `window.open` it.
 *   2. On a missing key, a `null` url in the response, or any request failure, fall back to the
 *      pre-existing links: `resolveInvoiceDocumentUrl` for invoices, the eligibility-resolved
 *      `pdf` field for credit notes.
 *
 * Both handlers guard against double-clicks with a simple in-flight id set — clicking the same
 * row again while its request is outstanding is a no-op rather than firing a second `window.open`.
 */
import { http } from '@/interceptors/axiosRequestInterceptor';
import { resolveInvoiceDocumentUrl, type InvoiceDocumentUrlFields } from './invoicePdf';

function openUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || url.trim() === '') return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

// ---------------------------------------------------------------------------
// Invoice PDFs — GET order-item-invoices/{id}/pdf-url
// ---------------------------------------------------------------------------

interface InvoicePdfUrlResult {
  url: string | null;
  fallback?: InvoiceDocumentUrlFields;
}

const invoiceInFlight = new Set<string>();

/** `true` when the row carries a stored S3 copy that's worth hitting the endpoint for. */
function hasStoredInvoicePdf(inv: Record<string, unknown> | null | undefined): boolean {
  const key = inv?.invoice_pdf_s3_key;
  if (typeof key === 'string' && key.trim() !== '') return true;
  // Legacy rows may only carry the already-signed URL field (pre-`_key` rollout) — still a stored
  // copy, still worth resolving through the endpoint for a non-stale signature.
  const legacyUrl = inv?.invoice_pdf_s3_url;
  return typeof legacyUrl === 'string' && legacyUrl.trim() !== '';
}

/**
 * Click handler for an invoice's PDF link. Resolves via the `pdf-url` endpoint when the row has a
 * stored S3 copy; otherwise (and on any endpoint failure) falls back to the existing
 * `resolveInvoiceDocumentUrl` ladder (S3-signed copy already on the row → hosted invoice page →
 * raw Stripe PDF link).
 */
export async function openInvoicePdf(inv: Record<string, unknown> | null | undefined): Promise<void> {
  const rec = inv ?? {};
  const idNum = Number(rec.id);

  if (!hasStoredInvoicePdf(rec) || !Number.isFinite(idNum) || idNum <= 0) {
    openUrl(resolveInvoiceDocumentUrl(rec));
    return;
  }

  const guardKey = String(idNum);
  if (invoiceInFlight.has(guardKey)) return;
  invoiceInFlight.add(guardKey);
  try {
    const response = await http.get<{ message: string; result: InvoicePdfUrlResult }>(
      `order-item-invoices/${idNum}/pdf-url`,
    );
    const result = response.data?.result;
    if (!openUrl(result?.url)) {
      openUrl(resolveInvoiceDocumentUrl(rec, result?.fallback));
    }
  } catch {
    // Best-effort: this is a "get a fresher link" optimization, not a user-facing action whose
    // failure needs its own toast — silently fall back to the existing ladder.
    openUrl(resolveInvoiceDocumentUrl(rec));
  } finally {
    invoiceInFlight.delete(guardKey);
  }
}

// ---------------------------------------------------------------------------
// Credit-note PDFs — GET order-customer-credits/{id}/pdf-url
// ---------------------------------------------------------------------------

interface CreditNotePdfUrlResult {
  url: string | null;
}

const creditNoteInFlight = new Set<string>();

/**
 * Click handler for a credit note's PDF link. `credit` accepts either the embedded
 * `order_item_invoices.credit_notes[]` payload entry (has `id` + `credit_note_pdf_s3_key`, the
 * endpoint-eligible shape) or a plain fallback link merged in by the caller (`pdf` — e.g. the
 * eligibility-resolved Stripe/S3 link `BillingDocumentsLedger` already carries per row).
 *
 * Resolves via the `pdf-url` endpoint when an S3 key + numeric id are present; otherwise (and on
 * any endpoint failure or `null` url) falls back to `credit.pdf`.
 */
export async function openCreditNotePdf(credit: Record<string, unknown> | null | undefined): Promise<void> {
  const rec = credit ?? {};
  const key = rec.credit_note_pdf_s3_key;
  const hasKey = typeof key === 'string' && key.trim() !== '';
  const idNum = Number(rec.id);

  if (!hasKey || !Number.isFinite(idNum) || idNum <= 0) {
    openUrl(typeof rec.pdf === 'string' ? rec.pdf : null);
    return;
  }

  const guardKey = String(idNum);
  if (creditNoteInFlight.has(guardKey)) return;
  creditNoteInFlight.add(guardKey);
  try {
    const response = await http.get<{ message: string; result: CreditNotePdfUrlResult }>(
      `order-customer-credits/${idNum}/pdf-url`,
    );
    if (!openUrl(response.data?.result?.url)) {
      openUrl(typeof rec.pdf === 'string' ? rec.pdf : null);
    }
  } catch {
    openUrl(typeof rec.pdf === 'string' ? rec.pdf : null);
  } finally {
    creditNoteInFlight.delete(guardKey);
  }
}
